import React, { useState } from 'react';

const VACCINES = [
    { key: 'rabia',      label: 'Rabia',      icon: '🦠' },
    { key: 'bordetella', label: 'Bordetella', icon: '🌬️' },
    { key: 'giardia',    label: 'Giardia',    icon: '🔬' },
    { key: 'sextuple',   label: 'Séxtuple',   icon: '💉' },
];

const MEDICAL_EVENTS = [
    { key: 'dentalProphylaxis', label: 'Profilaxis dental',  icon: '🦷' },
    { key: 'bloodCount',        label: 'Biometría hemática', icon: '🩸' },
    { key: 'bloodChemistry',    label: 'Química sanguínea',  icon: '🧪' },
];

// Extract YYYY-MM-DD from ISO string and parse as local to avoid UTC day shift
const fmtDate = (d) => {
    if (!d) return '';
    const str = typeof d === 'string' ? d : new Date(d).toISOString();
    const [year, month, day] = str.slice(0, 10).split('-');
    const local = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return local.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Audit timestamps represent a real moment in time — local timezone display is correct
const fmtAudit = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// For stored dates: extract YYYY-MM-DD from the ISO string (avoids UTC shift).
// For "today" (no arg): use local date components.
const toInput = (d) => {
    if (d) {
        const str = typeof d === 'string' ? d : new Date(d).toISOString();
        return str.slice(0, 10);
    }
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

// Build UTC-midnight ISO from a YYYY-MM-DD local date string (for date-only fields)
const localDateToISO = (dateStr) => new Date(`${dateStr}T00:00:00`).toISOString();

// Small inline audit line — matches the style of AuditLabel but compact
function AppliedBy({ user, date, action = 'Aplicado por' }) {
    if (!user?.username) return null;
    return (
        <span className="mc-applied-by">
            {action}: <strong>{user.username}</strong>
            {date && <> · {fmtAudit(date)}</>}
        </span>
    );
}

export default function MembershipCard({
    membership,
    hideHeader,
    onApplyVaccine,
    onAddDeworming, onUpdateDewormingSession, onRemoveDeworming,
    onAddBath,      onUpdateBathSession,      onRemoveBath,
    onAddVetAppointment, onUpdateVetAppointmentSession, onRemoveVetAppointment,
    onApplyMedicalEvent,
    onDelete
}) {
    // editing state: { type: 'vaccine'|'deworming'|'bath'|'vet'|'medical', key?: string, id?: string, date: string }
    const [editing, setEditing] = useState(null);
    const [addDewDate, setAddDewDate] = useState(toInput());
    const [addBathDate, setAddBathDate] = useState(toInput());
    const [addVetDate, setAddVetDate] = useState(toInput());
    const [busy, setBusy] = useState(false);

    const dog = membership.dog || {};
    const ownerName  = dog.owners?.[0]?.name  || '';
    const ownerPhone = dog.owners?.[0]?.phone || '';

    const now      = new Date();
    const end      = new Date(membership.endDate);
    const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    const isExpired = daysLeft <= 0 || membership.status !== 'active';

    const vaccinesApplied = VACCINES.filter(v => membership.vaccines?.[v.key]?.appliedAt).length;
    const dewCount  = membership.deworming?.length        || 0;
    const bathCount = membership.baths?.length            || 0;
    const vetCount  = membership.vetAppointments?.length  || 0;
    const medDone   = MEDICAL_EVENTS.filter(e => membership[e.key]?.appliedAt).length;

    const wrap = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

    /* ── Vaccine handlers ── */
    const startEditVaccine = (key, currentDate) => {
        setEditing({ type: 'vaccine', key, date: toInput(currentDate) });
    };

    const saveVaccine = (key) => {
        if (!editing) return;
        wrap(async () => {
            await onApplyVaccine(membership._id, key, localDateToISO(editing.date));
            setEditing(null);
        });
    };

    const applyVaccineToday = (key) => {
        wrap(() => onApplyVaccine(membership._id, key, localDateToISO(toInput())));
    };

    const clearVaccine = (key) => {
        wrap(() => onApplyVaccine(membership._id, key, null));
    };

    /* ── Session handlers ── */
    const startEditSession = (type, id, currentDate) => {
        setEditing({ type, id, date: toInput(currentDate) });
    };

    const saveSession = (type, id) => {
        if (!editing) return;
        wrap(async () => {
            const iso = localDateToISO(editing.date);
            if (type === 'deworming') await onUpdateDewormingSession(membership._id, id, iso);
            else if (type === 'vet')  await onUpdateVetAppointmentSession(membership._id, id, iso);
            else                      await onUpdateBathSession(membership._id, id, iso);
            setEditing(null);
        });
    };

    /* ── Medical event handlers ── */
    const applyMedicalEventToday = (key) => {
        wrap(() => onApplyMedicalEvent(membership._id, key, localDateToISO(toInput())));
    };

    const startEditMedical = (key, currentDate) => {
        setEditing({ type: 'medical', key, date: toInput(currentDate) });
    };

    const saveMedical = (key) => {
        if (!editing) return;
        wrap(async () => {
            await onApplyMedicalEvent(membership._id, key, localDateToISO(editing.date));
            setEditing(null);
        });
    };

    const clearMedical = (key) => {
        wrap(() => onApplyMedicalEvent(membership._id, key, null));
    };

    const isEditingField = (type, keyOrId) =>
        editing?.type === type && (editing.key === keyOrId || editing.id === keyOrId);

    return (
        <div className={`mc-card${isExpired ? ' mc-card--expired' : ''}`}>

            {/* ── Header ── */}
            {!hideHeader && (
            <div className="mc-header">
                <div className="mc-dog-info">
                    <div className="mc-avatar">
                        {dog.pictureUrl || dog.picture
                            ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                            : <span>{dog.name?.[0]?.toUpperCase() || '🐕'}</span>}
                    </div>
                    <div>
                        <h3 className="mc-dog-name">{dog.name || 'Perro'}</h3>
                        <p className="mc-dog-sub">{dog.breed}{ownerName ? ` · ${ownerName}` : ''}</p>
                        {ownerPhone && <p className="mc-dog-sub">{ownerPhone}</p>}
                    </div>
                </div>
                <div className="mc-status-col">
                    <span className={`mc-badge${isExpired ? ' mc-badge--expired' : ' mc-badge--active'}`}>
                        {isExpired ? 'Expirada' : 'Activa'}
                    </span>
                    {!isExpired && <span className="mc-days-left">{daysLeft}d</span>}
                </div>
            </div>
            )}

            {/* ── Compact status when header is hidden ── */}
            {hideHeader && (
                <div className="mc-status-compact">
                    <span className={`mc-badge${isExpired ? ' mc-badge--expired' : ' mc-badge--active'}`}>
                        {isExpired ? 'Expirada' : 'Activa'}
                    </span>
                    {!isExpired && <span className="mc-days-left">{daysLeft}d restantes</span>}
                </div>
            )}

            {/* ── Dates ── */}
            <div className="mc-dates">
                <span>📅 {fmtDate(membership.startDate)}</span>
                <span className="mc-dates-sep">→</span>
                <span>{fmtDate(membership.endDate)}</span>
            </div>

            {/* ── Progress chips ── */}
            <div className="mc-summary">
                <span className="mc-chip mc-chip--vaccine">{vaccinesApplied}/4 Vacunas</span>
                <span className="mc-chip mc-chip--dew">{dewCount}/4 Desparasitaciones</span>
                <span className="mc-chip mc-chip--bath">{bathCount}/3 Baños</span>
                <span className="mc-chip mc-chip--vet">{vetCount}/3 Consultas vet.</span>
                <span className="mc-chip mc-chip--med">{medDone}/3 Estudios</span>
            </div>

            {/* ── Vaccines ── */}
            <div className="mc-section">
                <p className="mc-section-title">💉 Vacunas</p>
                <div className="mc-list">
                    {VACCINES.map(({ key, label, icon }) => {
                        const vacc    = membership.vaccines?.[key];
                        const applied = vacc?.appliedAt;
                        const inEdit  = isEditingField('vaccine', key);

                        return (
                            <div key={key} className={`mc-list-item${applied ? ' mc-list-item--done' : ''}`}>
                                <div className="mc-list-left">
                                    <span className="mc-list-icon">{icon}</span>
                                    <span className="mc-list-label">{label}</span>
                                </div>

                                <div className="mc-list-right">
                                    {applied ? (
                                        inEdit ? (
                                            /* ── Edit mode ── */
                                            <div className="mc-edit-row">
                                                <input
                                                    type="date"
                                                    value={editing.date}
                                                    onChange={e => setEditing(prev => ({ ...prev, date: e.target.value }))}
                                                />
                                                <button className="mc-btn-save" disabled={busy} onClick={() => saveVaccine(key)}>Guardar</button>
                                                <button className="mc-btn-cancel" onClick={() => setEditing(null)}>✕</button>
                                            </div>
                                        ) : (
                                            /* ── Applied view ── */
                                            <div className="mc-applied-view">
                                                <div className="mc-applied-date-row">
                                                    <span className="mc-applied-date">{fmtDate(applied)}</span>
                                                    {!isExpired && (
                                                        <>
                                                            <button className="mc-btn-edit" disabled={busy} onClick={() => startEditVaccine(key, applied)} title="Editar fecha">✏️</button>
                                                            <button className="mc-btn-clear" disabled={busy} onClick={() => clearVaccine(key)} title="Quitar">✕</button>
                                                        </>
                                                    )}
                                                </div>
                                                <AppliedBy user={vacc.appliedBy} date={applied} />
                                            </div>
                                        )
                                    ) : isExpired ? (
                                        <span className="mc-pending-label">Pendiente</span>
                                    ) : (
                                        <button className="mc-btn-apply" disabled={busy} onClick={() => applyVaccineToday(key)}>
                                            Aplicar hoy
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Deworming ── */}
            <div className="mc-section">
                <p className="mc-section-title">🪱 Desparasitación interna ({dewCount}/4)</p>
                <div className="mc-list">
                    {Array.from({ length: 4 }).map((_, i) => {
                        const session = membership.deworming?.[i];
                        const inEdit  = session && isEditingField('deworming', session._id);

                        return (
                            <div key={session?._id || `dew-empty-${i}`} className={`mc-list-item${session ? ' mc-list-item--done' : ' mc-list-item--empty'}`}>
                                <div className="mc-list-left">
                                    <span className="mc-list-num">{i + 1}</span>
                                    {!session && <span className="mc-pending-label">Pendiente</span>}
                                </div>

                                {session && (
                                    <div className="mc-list-right">
                                        {inEdit ? (
                                            <div className="mc-edit-row">
                                                <input
                                                    type="date"
                                                    value={editing.date}
                                                    onChange={e => setEditing(prev => ({ ...prev, date: e.target.value }))}
                                                />
                                                <button className="mc-btn-save" disabled={busy} onClick={() => saveSession('deworming', session._id)}>Guardar</button>
                                                <button className="mc-btn-cancel" onClick={() => setEditing(null)}>✕</button>
                                            </div>
                                        ) : (
                                            <div className="mc-applied-view">
                                                <div className="mc-applied-date-row">
                                                    <span className="mc-applied-date">{fmtDate(session.appliedAt)}</span>
                                                    {!isExpired && (
                                                        <>
                                                            <button className="mc-btn-edit" disabled={busy} onClick={() => startEditSession('deworming', session._id, session.appliedAt)} title="Editar fecha">✏️</button>
                                                            <button className="mc-btn-clear" disabled={busy} onClick={() => wrap(() => onRemoveDeworming(membership._id, session._id))} title="Quitar">✕</button>
                                                        </>
                                                    )}
                                                </div>
                                                <AppliedBy user={session.appliedBy} date={session.appliedAt} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {!isExpired && dewCount < 4 && (
                    <div className="mc-add-row">
                        <input type="date" value={addDewDate} onChange={e => setAddDewDate(e.target.value)} />
                        <button className="mc-btn-add" disabled={busy}
                            onClick={() => wrap(() => onAddDeworming(membership._id, localDateToISO(addDewDate)))}>
                            + Aplicar
                        </button>
                    </div>
                )}
            </div>

            {/* ── Baths ── */}
            <div className="mc-section">
                <p className="mc-section-title">🛁 Baños ({bathCount}/3)</p>
                <div className="mc-list">
                    {Array.from({ length: 3 }).map((_, i) => {
                        const session = membership.baths?.[i];
                        const inEdit  = session && isEditingField('bath', session._id);

                        return (
                            <div key={session?._id || `bath-empty-${i}`} className={`mc-list-item${session ? ' mc-list-item--done' : ' mc-list-item--empty'}`}>
                                <div className="mc-list-left">
                                    <span className="mc-list-num">{i + 1}</span>
                                    {!session && <span className="mc-pending-label">Pendiente</span>}
                                </div>

                                {session && (
                                    <div className="mc-list-right">
                                        {inEdit ? (
                                            <div className="mc-edit-row">
                                                <input
                                                    type="date"
                                                    value={editing.date}
                                                    onChange={e => setEditing(prev => ({ ...prev, date: e.target.value }))}
                                                />
                                                <button className="mc-btn-save" disabled={busy} onClick={() => saveSession('bath', session._id)}>Guardar</button>
                                                <button className="mc-btn-cancel" onClick={() => setEditing(null)}>✕</button>
                                            </div>
                                        ) : (
                                            <div className="mc-applied-view">
                                                <div className="mc-applied-date-row">
                                                    <span className="mc-applied-date">{fmtDate(session.appliedAt)}</span>
                                                    {!isExpired && (
                                                        <>
                                                            <button className="mc-btn-edit" disabled={busy} onClick={() => startEditSession('bath', session._id, session.appliedAt)} title="Editar fecha">✏️</button>
                                                            <button className="mc-btn-clear" disabled={busy} onClick={() => wrap(() => onRemoveBath(membership._id, session._id))} title="Quitar">✕</button>
                                                        </>
                                                    )}
                                                </div>
                                                <AppliedBy user={session.appliedBy} date={session.appliedAt} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {!isExpired && bathCount < 3 && (
                    <div className="mc-add-row">
                        <input type="date" value={addBathDate} onChange={e => setAddBathDate(e.target.value)} />
                        <button className="mc-btn-add" disabled={busy}
                            onClick={() => wrap(() => onAddBath(membership._id, localDateToISO(addBathDate)))}>
                            + Usar baño
                        </button>
                    </div>
                )}
            </div>

            {/* ── Vet Appointments ── */}
            <div className="mc-section">
                <p className="mc-section-title">🩺 Consultas veterinarias ({vetCount}/3)</p>
                <div className="mc-list">
                    {Array.from({ length: 3 }).map((_, i) => {
                        const session = membership.vetAppointments?.[i];
                        const inEdit  = session && isEditingField('vet', session._id);

                        return (
                            <div key={session?._id || `vet-empty-${i}`} className={`mc-list-item${session ? ' mc-list-item--done' : ' mc-list-item--empty'}`}>
                                <div className="mc-list-left">
                                    <span className="mc-list-num">{i + 1}</span>
                                    {!session && <span className="mc-pending-label">Pendiente</span>}
                                </div>

                                {session && (
                                    <div className="mc-list-right">
                                        {inEdit ? (
                                            <div className="mc-edit-row">
                                                <input
                                                    type="date"
                                                    value={editing.date}
                                                    onChange={e => setEditing(prev => ({ ...prev, date: e.target.value }))}
                                                />
                                                <button className="mc-btn-save" disabled={busy} onClick={() => saveSession('vet', session._id)}>Guardar</button>
                                                <button className="mc-btn-cancel" onClick={() => setEditing(null)}>✕</button>
                                            </div>
                                        ) : (
                                            <div className="mc-applied-view">
                                                <div className="mc-applied-date-row">
                                                    <span className="mc-applied-date">{fmtDate(session.appliedAt)}</span>
                                                    {!isExpired && (
                                                        <>
                                                            <button className="mc-btn-edit" disabled={busy} onClick={() => startEditSession('vet', session._id, session.appliedAt)} title="Editar fecha">✏️</button>
                                                            <button className="mc-btn-clear" disabled={busy} onClick={() => wrap(() => onRemoveVetAppointment(membership._id, session._id))} title="Quitar">✕</button>
                                                        </>
                                                    )}
                                                </div>
                                                <AppliedBy user={session.appliedBy} date={session.appliedAt} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {!isExpired && vetCount < 3 && (
                    <div className="mc-add-row">
                        <input type="date" value={addVetDate} onChange={e => setAddVetDate(e.target.value)} />
                        <button className="mc-btn-add" disabled={busy}
                            onClick={() => wrap(() => onAddVetAppointment(membership._id, localDateToISO(addVetDate)))}>
                            + Registrar consulta
                        </button>
                    </div>
                )}
            </div>

            {/* ── Medical Events (dental, blood count, blood chemistry) ── */}
            <div className="mc-section">
                <p className="mc-section-title">🔬 Estudios y procedimientos</p>
                <div className="mc-list">
                    {MEDICAL_EVENTS.map(({ key, label, icon }) => {
                        const event   = membership[key];
                        const applied = event?.appliedAt;
                        const inEdit  = isEditingField('medical', key);

                        return (
                            <div key={key} className={`mc-list-item${applied ? ' mc-list-item--done' : ''}`}>
                                <div className="mc-list-left">
                                    <span className="mc-list-icon">{icon}</span>
                                    <span className="mc-list-label">{label}</span>
                                </div>

                                <div className="mc-list-right">
                                    {applied ? (
                                        inEdit ? (
                                            <div className="mc-edit-row">
                                                <input
                                                    type="date"
                                                    value={editing.date}
                                                    onChange={e => setEditing(prev => ({ ...prev, date: e.target.value }))}
                                                />
                                                <button className="mc-btn-save" disabled={busy} onClick={() => saveMedical(key)}>Guardar</button>
                                                <button className="mc-btn-cancel" onClick={() => setEditing(null)}>✕</button>
                                            </div>
                                        ) : (
                                            <div className="mc-applied-view">
                                                <div className="mc-applied-date-row">
                                                    <span className="mc-applied-date">{fmtDate(applied)}</span>
                                                    {!isExpired && (
                                                        <>
                                                            <button className="mc-btn-edit" disabled={busy} onClick={() => startEditMedical(key, applied)} title="Editar fecha">✏️</button>
                                                            <button className="mc-btn-clear" disabled={busy} onClick={() => clearMedical(key)} title="Quitar">✕</button>
                                                        </>
                                                    )}
                                                </div>
                                                <AppliedBy user={event.appliedBy} date={applied} />
                                            </div>
                                        )
                                    ) : isExpired ? (
                                        <span className="mc-pending-label">Pendiente</span>
                                    ) : (
                                        <button className="mc-btn-apply" disabled={busy} onClick={() => applyMedicalEventToday(key)}>
                                            Registrar hoy
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {membership.notes && (
                <div className="mc-notes">📝 {membership.notes}</div>
            )}

            {onDelete && (
                <div className="mc-footer">
                    <button className="mc-btn-delete" disabled={busy}
                        onClick={() => { if (window.confirm('¿Eliminar esta membresía?')) onDelete(membership._id); }}>
                        Eliminar membresía
                    </button>
                </div>
            )}
        </div>
    );
}
